import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { Cookie, Check, X, Settings, ShieldCheck, BarChart3, Megaphone } from "lucide-react";
import { useCookieConsent } from "@/hooks/useCookieConsent";

const categories = [
  { key: "necessary", icon: ShieldCheck, title: "Noodzakelijk", desc: "Vereist voor de basisfunctionaliteit (navigatie, formulieren, beveiliging). Kan niet worden uitgeschakeld.", locked: true },
  { key: "analytics", icon: BarChart3, title: "Statistiek", desc: "Helpen ons begrijpen hoe bezoekers de site gebruiken (anoniem). We gebruiken deze data om de website te verbeteren.", locked: false },
  { key: "marketing", icon: Megaphone, title: "Marketing", desc: "Om relevante advertenties en content te tonen op andere platforms (Meta, Google, LinkedIn). Alleen met jouw toestemming.", locked: false },
];

const Toggle = ({ checked, onChange, disabled, testid }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    disabled={disabled}
    onClick={() => !disabled && onChange(!checked)}
    data-testid={testid}
    className={`relative w-11 h-6 rounded-full transition-colors duration-300 ${
      checked ? "bg-electric" : disabled ? "bg-ivory-300 cursor-not-allowed" : "bg-ivory-400 hover:bg-ivory-500"
    }`}
  >
    <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-transform duration-300 ${checked ? "translate-x-5" : ""}`} />
  </button>
);

const CookieConsent = () => {
  const { consent, open, acceptAll, declineAll, saveCustom } = useCookieConsent();
  const [showDetails, setShowDetails] = useState(false);
  const [draft, setDraft] = useState({ analytics: consent.analytics, marketing: consent.marketing });

  useEffect(() => {
    setDraft({ analytics: consent.analytics, marketing: consent.marketing });
  }, [consent.analytics, consent.marketing]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          data-testid="cookie-consent-banner"
          className="fixed inset-x-0 bottom-0 z-[60] px-3 sm:px-6 pb-3 sm:pb-6 pointer-events-none"
        >
          <motion.div
            initial={{ y: 80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 80, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="relative max-w-3xl mx-auto pointer-events-auto rounded-2xl border border-ivory-400 bg-white shadow-card-lift overflow-hidden"
          >
            <div className="absolute top-0 left-0 right-0 h-1 bg-electric" />
            <div className="p-5 sm:p-6">
              {!showDetails ? (
                <div className="flex items-start gap-4">
                  <div className="hidden sm:grid w-11 h-11 shrink-0 rounded-xl bg-electric/10 place-items-center">
                    <Cookie className="w-5 h-5 text-electric" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h2 className="font-display text-lg sm:text-xl font-black text-coal mb-1">Cookies bij UPPIT</h2>
                    <p className="text-sm text-coal/60 leading-relaxed">
                      Wij gebruiken cookies om de website goed te laten werken en — met jouw toestemming — om bezoeken
                      anoniem te analyseren en relevante content te tonen. Lees ons{" "}
                      <Link to="/privacy" data-testid="cookie-privacy-link" className="text-electric hover:underline underline-offset-4">privacybeleid</Link> en{" "}
                      <Link to="/cookies" data-testid="cookie-policy-link" className="text-electric hover:underline underline-offset-4">cookiebeleid</Link> voor meer info.
                    </p>
                    <div className="mt-4 flex flex-col sm:flex-row gap-2">
                      <button onClick={acceptAll} data-testid="cookie-accept-all" className="inline-flex items-center justify-center gap-2 bg-electric hover:bg-electric-600 text-white font-semibold rounded-full px-5 py-2.5 text-sm transition-all">
                        <Check className="w-4 h-4" /> Accepteer alle
                      </button>
                      <button onClick={declineAll} data-testid="cookie-decline-all" className="inline-flex items-center justify-center gap-2 bg-ivory-200 hover:bg-ivory-300 border border-ivory-400 text-coal font-semibold rounded-full px-5 py-2.5 text-sm transition-all">
                        Alleen noodzakelijk
                      </button>
                      <button onClick={() => setShowDetails(true)} data-testid="cookie-customize" className="inline-flex items-center justify-center gap-2 text-coal/60 hover:text-electric font-semibold px-5 py-2.5 text-sm transition-colors">
                        <Settings className="w-4 h-4" /> Voorkeuren
                      </button>
                    </div>
                  </div>
                  <button onClick={declineAll} aria-label="Sluiten" data-testid="cookie-close" className="shrink-0 w-8 h-8 rounded-full grid place-items-center text-coal/40 hover:text-coal hover:bg-ivory-200 transition-colors">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div data-testid="cookie-details">
                  <div className="flex items-start justify-between gap-4 mb-4">
                    <div>
                      <h2 className="font-display text-lg sm:text-xl font-black text-coal mb-1">Cookievoorkeuren</h2>
                      <p className="text-xs text-coal/50">Kies welke categorieën je toestaat. Je kunt dit altijd aanpassen via de link onderaan de pagina.</p>
                    </div>
                    <button onClick={() => setShowDetails(false)} aria-label="Terug" data-testid="cookie-back" className="shrink-0 w-8 h-8 rounded-full grid place-items-center text-coal/40 hover:text-coal hover:bg-ivory-200 transition-colors">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="space-y-2 max-h-[40vh] overflow-y-auto pr-1">
                    {categories.map((cat) => {
                      const Icon = cat.icon;
                      const checked = cat.locked ? true : !!draft[cat.key];
                      return (
                        <div key={cat.key} className="flex items-start gap-3 p-3 rounded-xl border border-ivory-400 bg-ivory-100">
                          <div className="w-8 h-8 shrink-0 rounded-lg bg-electric/10 grid place-items-center text-electric">
                            <Icon className="w-4 h-4" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2 mb-1">
                              <div className="font-semibold text-coal text-sm">
                                {cat.title}
                                {cat.locked && <span className="ml-2 text-[10px] uppercase tracking-[0.16em] text-electric">Altijd actief</span>}
                              </div>
                              <Toggle checked={checked} disabled={cat.locked} onChange={(val) => setDraft((d) => ({ ...d, [cat.key]: val }))} testid={`cookie-toggle-${cat.key}`} />
                            </div>
                            <p className="text-xs text-coal/55 leading-relaxed">{cat.desc}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="mt-5 flex flex-col sm:flex-row gap-2 sm:justify-end">
                    <button onClick={declineAll} data-testid="cookie-details-decline" className="inline-flex items-center justify-center gap-2 bg-ivory-200 hover:bg-ivory-300 border border-ivory-400 text-coal font-semibold rounded-full px-5 py-2.5 text-sm transition-all">
                      Alleen noodzakelijk
                    </button>
                    <button onClick={() => saveCustom(draft)} data-testid="cookie-details-save" className="inline-flex items-center justify-center gap-2 bg-electric hover:bg-electric-600 text-white font-semibold rounded-full px-5 py-2.5 text-sm transition-all">
                      <Check className="w-4 h-4" /> Voorkeuren opslaan
                    </button>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieConsent;
